// PRPricing.js - New Zealand
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle } from 'lucide-react';

const PRPricing = () => {
  const coverageItems = [
    'An eligibility review and advice on the right category for you',
    'Calculating your SMC points and finding ways to lift them',
    'Preparing your EOI and helping you get it submitted',
    'A document checklist with guidance on each item on it',
    'Filling in the residence application forms and going over them with you',
    'Reviewing and verifying the supporting evidence',
    'Monitoring the case once it has been lodged',
    'Replying to Immigration NZ whenever they ask for more'
  ];

  const importantNotes = [
    'Visa application charges are payable to Immigration New Zealand directly',
    'Ivipass does not take payment of any government fee',
    'IELTS, PTE and TOEFL charges are settled with the test provider',
    'Panel physicians are paid directly for the medical examination',
    'Immigration New Zealand decides every residence application, not Ivipass. Our part is preparing a file that meets Immigration NZ requirements and evidences every claim in it. We do not promise an outcome, and you should be wary of anyone who does.'
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
              Our Fees for PR Support
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white p-8 rounded-lg shadow-sm mb-6 border border-gray-200"
          >
            <h3 className="text-xl font-semibold text-primary mb-4">
              Support with Your New Zealand Residence Application
            </h3>
            <p className="text-gray-600 text-base font-light leading-relaxed mb-6">
              The service takes in every part of a New Zealand residence application, beginning with an assessment of where you stand and ending when the application is filed.
            </p>

            <div className="mb-6">
              <p className="text-gray-700 text-base font-normal mb-4">
                Included in the service:
              </p>
              <div className="space-y-3">
                {coverageItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 size={18} className="text-gray-700 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-base font-light">
                      {item}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg mb-4 border border-gray-200">
              <p className="text-sm font-semibold text-primary mb-4">
                Service Fee
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                  <div>
                    <p className="text-lg font-semibold text-primary">PR Application Support</p>
                    <p className="text-sm text-gray-600">Preparation and lodgement of the full application</p>
                  </div>
                  <p className="text-2xl font-bold text-primary">$399</p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Note:</span> this covers our work on a single residence pathway. Applications for a partner or dependants may be charged separately.
                </p>
              </div>
            </div>

            <p className="text-gray-600 text-sm font-light leading-relaxed">
              The final figure is agreed with you after the consultation, once we know which category applies and what it will involve.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-6"
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-base font-semibold text-primary">
                Important notes:
              </p>
            </div>
            <div className="space-y-2 ml-8">
              {importantNotes.map((note, index) => (
                <p key={index} className="text-gray-700 text-sm font-light leading-relaxed">
                  • {note}
                </p>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-6"
          >
            <h4 className="text-base font-semibold text-primary mb-3">
              Government Charges (Payable to Immigration New Zealand)
            </h4>
            <div className="space-y-2 text-sm text-gray-700 font-light">
              <p>• <strong>Skilled Migrant Category:</strong> NZD $4,290 (primary applicant)</p>
              <p>• <strong>Work to Residence:</strong> NZD $4,290 (primary applicant)</p>
              <p>• <strong>Partnership Residence:</strong> NZD $1,958 (primary applicant)</p>
              <p>• <strong>Each additional applicant aged 18 or over:</strong> NZD $2,145</p>
              <p>• <strong>Each child under 18:</strong> NZD $1,073</p>
              <p className="text-xs text-gray-600 mt-3">
                These charges are revised periodically. Check the Immigration New Zealand website for the schedule applying at the time you lodge.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PRPricing;
